/**
 * Date/time formatting that always names the time zone.
 *
 * Sync watermarks and "last searched" stamps are only actionable if the reader
 * knows which zone they are in — "3:04 PM" is ambiguous between the viewer's
 * local time and the server's UTC. Every timestamp we render comes from the API
 * as an ISO string with an offset, so it is converted to the viewer's local zone
 * and labelled with that zone's short name (e.g. "PDT", "UTC", "GMT+2").
 *
 * Note: `dateStyle`/`timeStyle` cannot be combined with `timeZoneName` — that
 * throws a TypeError per ECMA-402 — hence the explicit component options.
 */

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

/**
 * Format an ISO timestamp in the viewer's local zone, with the zone named.
 *
 * @param value - ISO 8601 timestamp (or anything `Date` accepts).
 * @param fallback - Returned when the value is missing or unparseable.
 * @returns e.g. "Aug 4, 2026, 3:04 PM PDT"
 */
export function formatDateTimeWithZone(
  value: string | number | Date | null | undefined,
  fallback = '—',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString(undefined, DATE_TIME_OPTIONS);
}
